import * as d3 from 'd3';

// Layout helpers for RagSourceMapScreen (specs/rag-source-heatmap.md).
// Pure functions only — no React, no DOM — so the screen component stays
// focused on rendering and effects.

// Cluster placement order — the ÄÄNI (who's speaking) taxonomy
// (apps/backend/src/utils/ragVoiceClassification.js): public/official voices
// first, then research, then civil society/market, so the map reads as loose
// "themes" around the ring.
export const TYPE_ORDER = [
  'valtio', 'eduskunta', 'kansainvaliset',
  'tutkimus',
  'ajatushautomot', 'elinkeinoelama', 'kunnat',
];

// Neon/saturated variants of the Sitra brand palette (apps/display/src/config/brandColors.js).
// Must match apps/backend/src/utils/ragVoiceClassification.js's VOICE_COLORS.
export const TYPE_COLORS = {
  valtio: '#3B82F6',
  eduskunta: '#6366F1',
  kansainvaliset: '#A78BFA',
  tutkimus: '#22D3EE',
  ajatushautomot: '#FACC15',
  elinkeinoelama: '#FB7185',
  kunnat: '#34D399',
};

export function typeColor(voice) {
  return TYPE_COLORS[voice] || TYPE_COLORS.tutkimus;
}

// "Perusaineisto" — the always-on base dataset every Kuutio instance shares
// (rag_datasets.is_global = true). Pulled into its own central, formally-
// bordered sphere instead of being scattered across the publisher-type
// clusters, so facilitators can see "core corpus" vs. "this workshop's own
// research" at a glance. Bubbles keep their own publisher-type color even
// inside this cluster — only their position/boundary is special-cased.
export const BASE_CLUSTER_KEY = 'base';
export const BASE_CLUSTER_COLOR = '#FFD93D'; // Sitra brand yellow — formal/foundational accent

export function clusterKeyOf(node) {
  return node.isBaseDataset ? BASE_CLUSTER_KEY : (node.voice || 'tutkimus');
}

function orderedTypes(types) {
  const known = TYPE_ORDER.filter(t => types.includes(t));
  const rest = types.filter(t => !TYPE_ORDER.includes(t));
  return [...known, ...rest];
}

// One cluster center per cluster key, placed around the canvas center in
// TYPE_ORDER so related publisher types end up as visual neighbors. When a
// 'base' key is present it takes the exact center, and every other cluster
// rings around it — pushed out further than the base sphere is ever likely
// to grow (the forceAvoidCenter simulation force gives this a hard physical
// guarantee too, so a source never visually reads as belonging to both).
export function computeClusterCenters(clusterKeys, width, height, spreadFactor = 0.36) {
  const centers = {};
  if (!width || !height || clusterKeys.length === 0) return centers;
  const centerX = width / 2;
  const centerY = height / 2;
  const hasBase = clusterKeys.includes(BASE_CLUSTER_KEY);
  if (hasBase) {
    centers[BASE_CLUSTER_KEY] = { x: centerX, y: centerY };
  }
  const ringKeys = orderedTypes(clusterKeys.filter(k => k !== BASE_CLUSTER_KEY));
  const spread = Math.min(width, height) * spreadFactor;
  ringKeys.forEach((type, i) => {
    const angle = (i / ringKeys.length) * 2 * Math.PI - Math.PI / 2;
    centers[type] = {
      x: centerX + spread * Math.cos(angle),
      y: centerY + spread * Math.sin(angle),
    };
  });
  return centers;
}

// A smooth, padded outline around a cluster's current bubble positions.
// Falls back to a simple circle when there are too few points for a hull.
export function clusterHullPath(points, padding = 26) {
  if (points.length === 0) return null;
  if (points.length < 3) {
    const [cx, cy] = points.length === 1
      ? points[0]
      : [d3.mean(points, p => p[0]), d3.mean(points, p => p[1])];
    const r = padding + (points.length === 2 ? d3.max(points, p => Math.hypot(p[0] - cx, p[1] - cy)) : 0);
    return circlePath(cx, cy, r);
  }
  const hull = d3.polygonHull(points);
  if (!hull) return null;
  const [cx, cy] = d3.polygonCentroid(hull);
  const padded = hull.map(([x, y]) => {
    const dx = x - cx;
    const dy = y - cy;
    const dist = Math.hypot(dx, dy) || 1;
    const scale = (dist + padding) / dist;
    return [cx + dx * scale, cy + dy * scale];
  });
  const line = d3.line().curve(d3.curveCatmullRomClosed.alpha(0.8));
  return line(padded);
}

function circlePath(cx, cy, r) {
  return `M ${cx - r},${cy} a ${r},${r} 0 1,0 ${r * 2},0 a ${r},${r} 0 1,0 ${-r * 2},0`;
}

// A clean, formal circle (not an organic hull) around a set of points — used
// for the "Perusaineisto" sphere, which should read as a deliberate boundary
// rather than a loose cluster outline.
export function boundingCircle(points, padding = 34) {
  if (points.length === 0) return null;
  const cx = d3.mean(points, p => p[0]);
  const cy = d3.mean(points, p => p[1]);
  const r = padding + (d3.max(points, p => Math.hypot(p[0] - cx, p[1] - cy)) || 0);
  return { cx, cy, r };
}

// Where a cluster's group-name label goes: just outside the cluster's own
// bounding circle, in the direction away from the whole map's center. Ring
// clusters sit radially around the center, so "away from center" is reliably
// open canvas — never back toward the middle where other clusters' orbs
// live. The base (center) cluster has no real "outward" direction, so it
// falls back to straight up.
export function radialLabelAnchor(circle, canvasCenter, padding = 18, fallbackAngle = -Math.PI / 2) {
  const dx = circle.cx - canvasCenter.x;
  const dy = circle.cy - canvasCenter.y;
  const dist = Math.hypot(dx, dy);
  const [ux, uy] = dist > 1 ? [dx / dist, dy / dist] : [Math.cos(fallbackAngle), Math.sin(fallbackAngle)];
  return { x: circle.cx + ux * (circle.r + padding), y: circle.cy + uy * (circle.r + padding) };
}

// A one-sided custom d3-force: any node the simulation doesn't exempt gets
// pushed away from the map center whenever it drifts inside `keepOutRadius()`
// — never pulled inward if it's already further out, so it doesn't fight the
// cluster's own forceX/forceY anchor. Used to keep ring-cluster bubbles
// physically out of the (live, growing/shrinking) base sphere, so a source
// can never visually read as belonging to both clusters at once. `center`
// and `keepOutRadius` are accessor functions so the force reads live values
// every tick (the base sphere's radius changes as usage grows).
export function forceAvoidCenter({ center, keepOutRadius, isExempt }) {
  let nodes = [];
  function force(alpha) {
    const c = center();
    const keepOut = keepOutRadius();
    for (const d of nodes) {
      if (isExempt(d)) continue;
      const dx = d.x - c.x;
      const dy = d.y - c.y;
      const dist = Math.hypot(dx, dy) || 0.001;
      if (dist < keepOut) {
        const push = (keepOut - dist) * alpha * 0.6;
        d.vx += (dx / dist) * push;
        d.vy += (dy / dist) * push;
      }
    }
  }
  force.initialize = (_nodes) => { nodes = _nodes; };
  return force;
}

// Minimum-spanning-tree edges over a cluster's node positions (Prim's
// algorithm) — a light "constellation" of connections instead of a messy
// fully-connected graph.
export function clusterMstEdges(nodes) {
  if (nodes.length < 2) return [];
  const inTree = new Set([0]);
  const edges = [];
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  while (inTree.size < nodes.length) {
    let best = null;
    for (const i of inTree) {
      for (let j = 0; j < nodes.length; j++) {
        if (inTree.has(j)) continue;
        const d = dist(nodes[i], nodes[j]);
        if (!best || d < best.d) best = { i, j, d };
      }
    }
    if (!best) break;
    edges.push([best.i, best.j]);
    inTree.add(best.j);
  }
  return edges.map(([i, j]) => [nodes[i], nodes[j]]);
}

// How much text (labels, cluster titles) grows with zoom — capped and
// damped, not 1:1 with `k`, so it reads like a restrained UI element (in the
// same spirit as the hover tooltip's size) rather than ballooning to 6x its
// base size at max zoom.
export function labelFontScale(k) {
  return Math.min(1 + (k - 1) * 0.35, 1.8);
}

// Zoom tiers for the "more titles as you zoom in" reveal: fully zoomed out
// shows no source titles at all (only cluster titles), then progressively
// more of the biggest sources get one as you zoom past each threshold.
const ZOOM_LABEL_TIERS = [
  { minK: 3.6, percentile: 1 },
  { minK: 2.2, percentile: 0.5 },
  { minK: 1.05, percentile: 0.2 },
];

export function labelTierForScale(scale) {
  const tier = ZOOM_LABEL_TIERS.find(t => scale >= t.minK);
  return tier || { percentile: 0 };
}

// The default "title slot" for a bubble — just below it — shared by the
// persistent label placement below AND by the hover tooltip, so a tooltip on
// an unlabeled orb appears exactly where its label would if it had one
// (never a second, differently-placed box).
const LABEL_GAP = 10;
export function defaultLabelYOffset(node, scale = 1) {
  return node.r + LABEL_GAP / scale;
}

// Which nodes get a persistent text label, and where: the biggest sources
// within the current zoom tier's percentile first, anchored just below their
// own bubble by default. When that default spot would overlap an
// already-placed label, the label is nudged straight down in small steps
// (not hidden) so it still shows once there's room — labels stay vertically
// aligned with their bubble instead of hiding or jumping sideways. Widths
// are approximate (monospace char count) since we lay this out before the
// DOM has real text metrics.
//
// `scale` is the current zoom level: since zoom is a uniform transform,
// overlap in un-zoomed coordinates is scale-invariant, so footprints shrink
// by 1/scale as you zoom in — part of what makes more titles fit.
//
// Returns a Map<documentId, { yOffset }> — yOffset is the y distance from
// the bubble's own center to the label's top edge.
export function pickLabeledNodes(nodes, { charWidth = 6.2, scale = 1, percentile = 1, hardCap = 120 } = {}) {
  if (percentile <= 0 || nodes.length === 0) return new Map();
  const sorted = [...nodes].sort((a, b) => b.totalRetrievals - a.totalRetrievals);
  const candidateCount = Math.min(hardCap, Math.ceil(sorted.length * percentile));
  const candidates = sorted.slice(0, candidateCount);

  const placed = [];
  const positions = new Map();
  const h = 20 / scale;
  const step = h + 4 / scale;
  const maxAttempts = 4;

  for (const node of candidates) {
    const text = truncateTitle(node.title);
    const w = (Math.min(text.length * charWidth, 150) + 12) / scale;
    for (let attempt = 0; attempt <= maxAttempts; attempt++) {
      const yOffset = defaultLabelYOffset(node, scale) + attempt * step;
      const y = node.y + yOffset;
      const box = { x0: node.x - w / 2, x1: node.x + w / 2, y0: y, y1: y + h };
      const overlaps = placed.some(p => box.x0 < p.x1 && box.x1 > p.x0 && box.y0 < p.y1 && box.y1 > p.y0);
      if (!overlaps) {
        placed.push(box);
        positions.set(node.documentId, { yOffset });
        break;
      }
    }
  }
  return positions;
}

export function truncateTitle(title, max = 28) {
  if (!title) return '';
  return title.length > max ? `${title.slice(0, max - 1)}…` : title;
}

export function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, ch => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[ch]));
}

// Keeps pan/zoom within the map's own bounds (content fills [0,width]x[0,height]
// at scale 1, so nothing to pan when fully zoomed out). With `resistance` > 0,
// a transform that overshoots those bounds is only partly applied — an
// elastic "rubber band" instead of a hard wall — for touch drags in progress.
// Pass `resistance = 0` to hard-snap back into bounds (e.g. on gesture end).
export function constrainPan(transform, width, height, resistance = 0) {
  if (!width || !height) return transform;
  const { k } = transform;
  const minX = width * (1 - k);
  const maxX = 0;
  const minY = height * (1 - k);
  const maxY = 0;
  const clamp = (v, lo, hi) => {
    if (v < lo) return resistance > 0 ? lo - (lo - v) * resistance : lo;
    if (v > hi) return resistance > 0 ? hi + (v - hi) * resistance : hi;
    return v;
  };
  return new d3.ZoomTransform(k, clamp(transform.x, minX, maxX), clamp(transform.y, minY, maxY));
}

// Local (un-transformed) map coordinates → screen pixels, given the current
// pan/zoom transform ({k,x,y}). The label and cluster-title text layers live
// outside the zoomed <g> (so their font size can be set explicitly rather
// than relying on foreignObject content to visually scale with an SVG
// ancestor transform — inconsistent across browsers), so anything they
// render has to convert through this.
export function toScreenPoint(x, y, transform) {
  return { x: x * transform.k + transform.x, y: y * transform.k + transform.y };
}
